// Central content data for Adya Nutrition

export const SPECIALTIES = [
  {
    num: "01",
    title: "Gastrointestinal Disorders",
    tag: "Primary Focus",
    blurb:
      "Deep, targeted care for IBS (a sensitive gut that cramps, bloats & causes irregular bowels), IBD (long-term inflammation & ulcers in the gut, like Crohn's or colitis), GERD (stomach acid rising up, causing heartburn), SIBO (too many bacteria growing in the small intestine, causing gas & bloating), chronic bloating (ongoing tight, gassy, swollen tummy) and acid reflux. We restore the gut through evidence-based protocols and patient microbiome (the trillions of good bacteria living in your gut) rebuilding.",
    image: "https://images.unsplash.com/photo-1540420773420-3366772f4999",
  },
  {
    num: "02",
    title: "Metabolic Health",
    tag: "Diabetes & Hypertension",
    blurb:
      "Medical Nutrition Therapy (treating health conditions through a proper diet plan) for insulin resistance, Type 2 diabetes (trouble controlling blood sugar) and hypertension (high blood pressure) — stabilising your metabolism without deprivation or fad diets.",
    image: "https://images.pexels.com/photos/7578797/pexels-photo-7578797.jpeg",
  },
  {
    num: "03",
    title: "Autoimmune Conditions",
    tag: "Root-Cause Care",
    blurb:
      "For autoimmune conditions (when the body's defences mistakenly attack itself), we calm inflammation and support immune balance through gut-centred, anti-inflammatory (foods that calm swelling & irritation in the body) nutrition strategies tailored to your labs and symptoms.",
    image: "https://images.pexels.com/photos/7579831/pexels-photo-7579831.jpeg",
  },
  {
    num: "04",
    title: "Personalized Meal Planning",
    tag: "Practical & Sustainable",
    blurb:
      "Custom diet charts built around your labs, culture, taste and routine — nutrition that fits your real life, not a template you abandon in a week.",
    image: "https://images.unsplash.com/photo-1540420773420-3366772f4999",
  },
];

export const SERVICES = [
  {
    num: "01",
    title: "Customized Diet Charts",
    desc:
      "Fully personalised nutrition plans built from your clinical history, lab work and lifestyle. Structured, sustainable and continuously refined as you heal.",
    points: ["Lab-informed macro & micro targets", "Culturally-aligned food lists", "Weekly adjustments"],
  },
  {
    num: "02",
    title: "1-on-1 Nutritional Counseling",
    desc:
      "Private, empathetic consultations where we decode your symptoms, set realistic goals and build a partnership grounded in science and trust.",
    points: ["60-minute deep-dive intake", "Ongoing symptom tracking", "Direct clinician access"],
  },
  {
    num: "03",
    title: "Gut Health Programs",
    desc:
      "Structured multi-week protocols for IBS (a sensitive gut that cramps, bloats & causes irregular bowels), SIBO (too many bacteria growing in the small intestine, causing gas & bloating), IBD (long-term inflammation & ulcers in the gut, like Crohn's or colitis) and reflux — combining elimination, reintroduction and microbiome restoration phases.",
    points: ["Guided low-FODMAP (a diet that removes certain gas-forming foods to ease IBS) protocols", "SIBO breath-test interpretation", "Microbiome rebuilding"],
  },
];

export const CONCERNS = [
  "IBS / Chronic Bloating",
  "IBD (Crohn's / Colitis)",
  "GERD / Acid Reflux",
  "SIBO",
  "Diabetes / Metabolic Health",
  "Hypertension",
  "Autoimmune Condition",
  "Personalized Meal Plan",
  "Other",
];

export const TIME_SLOTS = [
  "09:00 AM",
  "10:30 AM",
  "12:00 PM",
  "02:00 PM",
  "03:30 PM",
  "05:00 PM",
];

export const TESTIMONIALS = [
  {
    name: "Ananya Iyer",
    location: "Bengaluru",
    concern: "IBS & Chronic Bloating",
    quote:
      "After years of unpredictable bloating, I finally understand my gut. The plan was precise, doable, and rooted in real science. Three months in, my symptoms have dropped dramatically.",
  },
  {
    name: "Rohan Deshpande",
    location: "Pune",
    concern: "SIBO",
    quote:
      "The low-FODMAP and SIBO protocol was explained so clearly that I never felt lost. Compassionate, structured and genuinely knowledgeable — this is the care I wish I'd found years ago.",
  },
  {
    name: "Meera Nair",
    location: "Kochi",
    concern: "GERD & Acid Reflux",
    quote:
      "My reflux used to control my life. With a personalised diet chart and steady guidance, I can eat with confidence again. I feel heard at every single consultation.",
  },
  {
    name: "Aditya Sharma",
    location: "Delhi",
    concern: "Type 2 Diabetes",
    quote:
      "My HbA1c improved more in four months than in the previous two years. No fads, no starving — just sustainable, culturally-familiar meals that actually fit my routine.",
  },
  {
    name: "Priya Menon",
    location: "Chennai",
    concern: "Autoimmune (Hashimoto's)",
    quote:
      "The anti-inflammatory approach calmed my flare-ups and my energy is finally back. Thoughtful, patient and deeply supportive throughout my healing.",
  },
  {
    name: "Kabir Malhotra",
    location: "Mumbai",
    concern: "IBD (Ulcerative Colitis)",
    quote:
      "Managing colitis felt overwhelming until I had a clear nutritional roadmap. The gut restoration program brought real, measurable relief — and a lot of reassurance.",
  },
];

export const FAQS = [
  {
    q: "Can nutrition really help my IBS, or do I need medication?",
    a: "For many people, targeted nutrition is one of the most effective ways to calm IBS. We identify your specific trigger foods and rebuild gut health with evidence-based protocols. We work alongside your doctor — never replacing prescribed medication, but often reducing how much you rely on it.",
  },
  {
    q: "Do you offer online / telehealth consultations?",
    a: "Yes. Consultations are available both at the clinic and online, so you can get the same personalised care from anywhere in India. Your diet charts and follow-ups are all shared digitally.",
  },
  {
    q: "I'm on a GLP-1 medication (like Ozempic). Can you help me eat well?",
    a: "Absolutely — this is one of today's most common needs. When appetite drops, protecting muscle and getting enough protein, fibre and nutrients becomes critical. We build a plan that works with your medication so your results last and your gut stays healthy.",
  },
  {
    q: "How is your diet plan different from free plans I find online?",
    a: "Generic plans ignore your body. Yours is built from your medical history, lab work, symptoms, culture and daily routine — then adjusted as you heal. It's clinical care, not a one-size-fits-all template you abandon in a week.",
  },
  {
    q: "Is the low-FODMAP diet something I'll have to follow forever?",
    a: "No — and that's a common myth. Low-FODMAP (a short-term diet that removes certain gas-forming foods) is a diagnostic tool, not a life sentence. We use a short elimination phase, then carefully reintroduce foods to find your personal tolerances and restore the widest, most enjoyable diet possible.",
  },
  {
    q: "Will your plans work with Indian and vegetarian food?",
    a: "Yes. Every plan is built around the foods you actually eat — vegetarian, non-vegetarian, regional cuisines and family meals included. Nutrition only works when it fits your real life.",
  },
  {
    q: "How soon will I see results?",
    a: "Many clients notice meaningful relief from bloating and discomfort within a few weeks, while deeper gut restoration and metabolic changes build over a few months. We track your progress and adjust continuously so you always know it's working.",
  },
  {
    q: "What happens in the first consultation?",
    a: "We take time to understand your full story — symptoms, history, labs and lifestyle. You'll leave with clarity on what's driving your issues and the first steps of a plan built specifically for you. Book a consultation and we'll take it from there.",
  },
];


